import {
  ChangeDetectionStrategy,
  Component,
  computed, inject,
  input,
  output,
} from '@angular/core';

import {
  CheckInDisplayItem,
  CheckInEvent,
} from './check-ins.model';
import {
  AnimalEventStatus,
  resolveAnimalEventStatus,
} from '../../utils/animal-event-status.util';
import {
  getDateTimestamp,
} from '../../utils/date-format-utc.util';
import { IconComponent } from "../../design-system/components/icon/icon.component";
import { DatesHandlerService } from "../../services/dates-handler.service";
import { NewLoadingComponent } from "../../new-loading/new-loading.component";
import { SurveySubmission } from "../../shared-models/survey-submission.model";
import { CheckInSummaryModel } from "../../check-ins-summary/check-ins-statuses";
import * as _ from "lodash";
import { generateCheckInStatusIcon } from "../../utils/check-ins-summary.util";
import { CheckInsSummaryComponent } from "../../check-ins-summary/check-ins-summary.component";

@Component({
  selector: 'lib-check-ins',
  standalone: true,
  imports: [
    IconComponent,
    NewLoadingComponent,
    CheckInsSummaryComponent
  ],
  templateUrl: './check-ins.component.html',
  styleUrl: './check-ins.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckInsComponent {
  readonly datesHandlerService = inject(DatesHandlerService)

  readonly events = input<readonly CheckInEvent[]>([]);

  readonly isLoading = input(false);

  readonly errorMessage = input<string | null>(null);

  readonly allowOwnerNavigation = input(false);

  readonly app = input<number>(0);

  readonly petSurveySubmissions = input<SurveySubmission[] | null>(null);

  readonly viewOwner = output<string>();

  readonly checkIns = computed<CheckInDisplayItem[]>(() => {
    const ownerGroups = this.groupEventsByOwner(this.events());

    return Array.from(ownerGroups.entries())
      .sort(([, leftEvents], [, rightEvents]) => {
        const leftLatestEvent = leftEvents[leftEvents.length - 1];

        const rightLatestEvent = rightEvents[rightEvents.length - 1];

        return (
          getDateTimestamp(rightLatestEvent?.eventDate) -
          getDateTimestamp(leftLatestEvent?.eventDate)
        );
      })
      .map(([ownerKey, ownerEvents]) =>
        this.createCheckIn(ownerKey, ownerEvents),
      );
  });

  readonly surveySubmissionsFormatted = computed(():
  { ownerId: string, checkInsSummary: CheckInSummaryModel[] }[] => {
    const submissions = this.petSurveySubmissions();
    if (!submissions) {
      return [];
    }

    const groupedByOwner = _.groupBy(submissions, s => s.petszelOwnerId.trim());

    return Object.keys(groupedByOwner).map(key => {
      const surveys = groupedByOwner[key];

      return {
        ownerId: key,
        checkInsSummary: surveys.map(s => ({
          surveyId: s.surveyId,
          surveyTitle: s.surveyTitle,
          statusIcon: generateCheckInStatusIcon(s.surveyTitle, s.partialSubmission, s.submissionData.surveyData)
        }))
      };
    });
  });

  getCheckInsByOwner(ownerId: string): CheckInSummaryModel[] {
    return this.surveySubmissionsFormatted().find(s => s.ownerId === ownerId)?.checkInsSummary ?? [];
  }

  isAdopterUnknown(e: CheckInDisplayItem): boolean {
    return e.ownerName.toLowerCase().includes('unknown');
  }

  onViewOwner(petszelOwnerId: string): void {
    const normalizedId = petszelOwnerId.trim();

    if (!this.allowOwnerNavigation() || !normalizedId) {
      return;
    }

    this.viewOwner.emit(normalizedId);
  }

  private groupEventsByOwner(
    events: readonly CheckInEvent[],
  ): Map<string, CheckInEvent[]> {
    const groups = new Map<string, CheckInEvent[]>();

    events.forEach((event, index) => {
      const ownerId = event.petszelOwnerId?.trim();

      /*
       * Events without an owner ID remain separate
       * instead of accidentally being grouped.
       */
      const ownerKey = ownerId || event.id?.trim() || `unknown-owner-${index}`;

      const existingEvents = groups.get(ownerKey) ?? [];

      existingEvents.push(event);

      groups.set(ownerKey, existingEvents);
    });

    groups.forEach((ownerEvents) => {
      ownerEvents.sort(
        (left, right) =>
          getDateTimestamp(left.eventDate) - getDateTimestamp(right.eventDate),
      );
    });

    return groups;
  }

  private createCheckIn(
    ownerKey: string,
    events: CheckInEvent[],
  ): CheckInDisplayItem {
    const lastEvent = events[events.length - 1];
    const rawStatus =
      lastEvent.petszelStatus?.trim() || 'Current';
    const status = resolveAnimalEventStatus(rawStatus);

    return {
      id: `${ownerKey}-` + `${lastEvent.id}`,
      petszelOwnerId: lastEvent.petszelOwnerId?.trim() || '',
      petszelAnimalId: lastEvent.petszelAnimalId?.trim() || '',
      ownerName: this.formatOwnerName(lastEvent),
      status: status.label,
      statusClass: this.getStatusClass(status.status),
      eventsFormatted: events.map(e => ({
        event: e.event,
        eventDate: e.eventDate ? this.datesHandlerService.formatUTCToCST(e.eventDate) : '',
        subEvent: e.subEvent,
        createdDate: e.createdDate ? this.datesHandlerService.formatUTCToCST(e.createdDate) : ''
      }))
    };
  }

  private formatOwnerName(event: CheckInEvent): string {
    const name = [event.firstname?.trim(), event.lastname?.trim()]
      .filter(Boolean)
      .join(' ');

    return name || 'Unknown pet parent';
  }

  private getStatusClass(status: AnimalEventStatus): string {
    return `check-ins__status--${status}`;
  }
}
